import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Upload, X, Image as ImageIcon, Eye, EyeOff, Settings, Clock, Grid3X3, Tag, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminHero = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [config, setConfig] = useState({ slides_to_show: 3, rotation_interval: 6, selection_interval: 12 });
  const [form, setForm] = useState({
    image: '', title: '', subtitle: '', description: '', category: '', order: 0, active: true
  });

  useEffect(() => { 
    fetchSlides(); 
    fetchConfig();
    fetchCategories();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await adminFetch(`${API}/admin/hero-slides`);
      const text = await response.text();
      
      if (response.ok) {
        try {
          const data = JSON.parse(text);
          setSlides(data);
        } catch {
          console.error('Error parsing slides JSON');
        }
      } else {
        console.error('Error fetching slides');
        toast.error('Error al cargar imágenes');
      }
    } catch (error) {
      console.error('Fetch slides error:', error);
      toast.error('Error al cargar imágenes');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await adminFetch(`${API}/admin/hero-config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminFetch(`${API}/admin/hero-categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Escribe un nombre para la categoría');
      return;
    }
    
    try {
      const response = await adminFetch(`${API}/admin/hero-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      
      if (response.ok) {
        toast.success('Categoría creada');
        setNewCategoryName('');
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear categoría');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!confirm('¿Eliminar esta categoría? Se quitará de todas las imágenes que la usen.')) return;
    
    try {
      const response = await adminFetch(`${API}/admin/hero-categories/${categoryId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Categoría eliminada');
        fetchCategories();
        fetchSlides();
      } else {
        toast.error('Error al eliminar categoría');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const saveConfig = async () => {
    try {
      const response = await adminFetch(`${API}/admin/hero-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        toast.success('Configuración guardada');
        setShowConfigModal(false);
      } else {
        toast.error('Error al guardar configuración');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const openModal = (slide = null) => {
    if (slide) {
      setEditingSlide(slide);
      setForm({
        image: slide.image, title: slide.title, subtitle: slide.subtitle,
        description: slide.description, category: slide.category || '', order: slide.order, active: slide.active
      });
    } else {
      setEditingSlide(null);
      setForm({ image: '', title: '', subtitle: '', description: '', category: '', order: slides.length, active: true });
    }
    setShowModal(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', 'hero');
    if (form.title) formData.append('filename_hint', form.title);

    try {
      const response = await adminFetch(`${API}/admin/upload`, {
        method: 'POST',
        body: formData
      });
      
      // Leemos el body una sola vez
      const text = await response.text();
      
      if (response.ok) {
        try {
          const data = JSON.parse(text);
          setForm(prev => ({ ...prev, image: data.url }));
          toast.success('Imagen subida correctamente');
        } catch {
          toast.error('Error al procesar la respuesta');
        }
      } else {
        toast.error('Error al subir imagen');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error de conexión al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image || !form.title) {
      toast.error('La imagen y el título son obligatorios');
      return;
    }

    const url = editingSlide 
      ? `${API}/admin/hero-slides/${editingSlide.slide_id}` 
      : `${API}/admin/hero-slides`;
    
    try {
      const response = await adminFetch(url, {
        method: editingSlide ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      // Solo verificamos el status, NO leemos el body
      if (response.ok) {
        toast.success(editingSlide ? 'Imagen actualizada' : 'Imagen añadida');
        setShowModal(false);
        setEditingSlide(null);
        setForm({ image: '', title: '', subtitle: '', description: '', category: '', order: 0, active: true });
        fetchSlides();
        fetchCategories();
      } else {
        toast.error('Error al guardar la imagen');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleDelete = async (slideId) => {
    if (!confirm('¿Eliminar esta imagen?')) return;

    try {
      const response = await adminFetch(`${API}/admin/hero-slides/${slideId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Imagen eliminada');
        fetchSlides();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async (slide) => {
    try {
      await adminFetch(`${API}/admin/hero-slides/${slide.slide_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !slide.active })
      });
      fetchSlides();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl">Imágenes de Inicio</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona las imágenes del carrusel de inicio. Máximo 12 imágenes.
          </p>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Grid3X3 className="w-4 h-4" />
              {config.slides_to_show} imágenes
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Transición cada {config.rotation_interval}s
            </span>
            <span className="flex items-center gap-1">
              <RefreshCw className="w-4 h-4" />
              Cambian cada {config.selection_interval}h
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowCategoryModal(true)}
            data-testid="hero-categories-btn"
          >
            <Tag className="w-4 h-4 mr-2" />
            Categorías ({categories.length})
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowConfigModal(true)}
            data-testid="hero-config-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configuración
          </Button>
          <Button 
            onClick={() => openModal()} 
            disabled={slides.length >= 12}
            data-testid="add-hero-slide"
          >
            <Plus className="w-4 h-4 mr-2" />
            Añadir ({slides.length}/12)
          </Button>
        </div>
      </div>

      {/* Slides Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="aspect-video bg-muted animate-pulse" />
          ))}
        </div>
      ) : slides.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border">
          <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No hay imágenes configuradas</p>
          <Button onClick={() => openModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Añadir Primera Imagen
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {slides.map((slide, index) => (
            <motion.div
              key={slide.slide_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`relative group bg-card border border-border overflow-hidden ${!slide.active ? 'opacity-50' : ''}`}
            >
              <div className="aspect-video relative">
                <img 
                  src={slide.image} 
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="icon" variant="secondary" onClick={() => openModal(slide)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="secondary" onClick={() => toggleActive(slide)}>
                    {slide.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="destructive" onClick={() => handleDelete(slide.slide_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-muted px-2 py-0.5">{index + 1}</span>
                  <h3 className="font-medium text-sm truncate">{slide.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-1">{slide.subtitle}</p>
                {slide.category && (
                  <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 mt-1">
                    <Tag className="w-3 h-3" />
                    {slide.category}
                  </span>
                )}
                {!slide.active && (
                  <span className="text-xs text-amber-500 mt-1 block">Desactivada</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="font-serif text-xl">
                {editingSlide ? 'Editar Imagen' : 'Nueva Imagen'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Image Upload */}
              <div>
                <label className="text-xs text-muted-foreground block mb-2">Imagen *</label>
                {form.image ? (
                  <div className="relative aspect-video bg-muted">
                    <img src={form.image} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                      className="absolute top-2 right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-border cursor-pointer hover:border-foreground/50">
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                    {uploading ? (
                      <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                        <span className="text-sm text-muted-foreground">Subir imagen</span>
                      </>
                    )}
                  </label>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Título *</label>
                <Input 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="Ej: Bodas"
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Subtítulo</label>
                <Input 
                  value={form.subtitle} 
                  onChange={e => setForm({...form, subtitle: e.target.value})}
                  placeholder="Ej: Capturando el amor en cada momento"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Descripción</label>
                <Textarea 
                  value={form.description} 
                  onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="Breve descripción que aparecerá en el carrusel"
                  rows={3}
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Categoría
                  <span className="text-muted-foreground/60 normal-case ml-2">(para evitar duplicados)</span>
                </label>
                <select
                  value={form.category}
                  onChange={e => setForm({...form, category: e.target.value})}
                  className="w-full h-10 px-3 bg-background border border-input text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Sin categoría</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  No se mostrarán dos imágenes de la misma categoría simultáneamente.
                  {categories.length === 0 && (
                    <button 
                      type="button" 
                      onClick={() => { setShowModal(false); setShowCategoryModal(true); }}
                      className="ml-1 text-primary underline"
                    >
                      Crear categorías
                    </button>
                  )}
                </p>
              </div>

              {/* Order & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Orden</label>
                  <Input 
                    type="number"
                    value={form.order} 
                    onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})}
                    min={0}
                  />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <Switch 
                    checked={form.active}
                    onCheckedChange={checked => setForm({...form, active: checked})}
                  />
                  <label className="text-sm">Activa</label>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  {editingSlide ? 'Guardar Cambios' : 'Añadir Imagen'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background border border-border w-full max-w-md"
          >
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="font-serif text-xl">Configuración del Carrusel</h2>
              <button onClick={() => setShowConfigModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Number of slides */}
              <div>
                <label className="text-sm font-medium block mb-2">
                  Número de imágenes a mostrar
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="6"
                    value={config.slides_to_show}
                    onChange={e => setConfig({...config, slides_to_show: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="w-12 text-center text-2xl font-serif">{config.slides_to_show}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Cuántas imágenes se mostrarán en el carrusel
                </p>
              </div>

              {/* Rotation interval (animation) */}
              <div>
                <label className="text-sm font-medium block mb-2">
                  Transición entre imágenes (segundos)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="3"
                    max="30"
                    step="1"
                    value={config.rotation_interval}
                    onChange={e => setConfig({...config, rotation_interval: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="w-16 text-center text-2xl font-serif">{config.rotation_interval}s</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Cada cuántos segundos pasa a la siguiente imagen (animación del carrusel)
                </p>
              </div>

              {/* Selection interval (new set of images) */}
              <div>
                <label className="text-sm font-medium block mb-2">
                  Renovación de imágenes (horas)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="168"
                    step="1"
                    value={config.selection_interval}
                    onChange={e => setConfig({...config, selection_interval: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="w-20 text-center text-2xl font-serif">
                    {config.selection_interval < 24 
                      ? `${config.selection_interval}h`
                      : `${Math.round(config.selection_interval / 24)}d`
                    }
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Cada cuántas horas se eligen nuevas imágenes aleatorias ({config.selection_interval}h = {(config.selection_interval / 24).toFixed(1)} días)
                </p>
              </div>

              {/* Info */}
              <div className="bg-muted/50 p-4 border border-border">
                <h4 className="text-sm font-medium mb-2">Lógica de selección</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Las imágenes se seleccionan aleatoriamente de las activas</li>
                  <li>• No se mostrarán dos imágenes de la misma categoría</li>
                  <li>• La selección cambia cada {config.selection_interval} horas</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowConfigModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveConfig} className="flex-1">
                  Guardar Configuración
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Categories Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background border border-border w-full max-w-md"
          >
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="font-serif text-xl">Gestión de Categorías</h2>
              <button onClick={() => setShowCategoryModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Add new category */}
              <div>
                <label className="text-sm font-medium block mb-2">Nueva categoría</label>
                <div className="flex gap-2">
                  <Input
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    placeholder="Ej: Bodas, Retratos, Naturaleza"
                    onKeyDown={e => e.key === 'Enter' && createCategory()}
                  />
                  <Button onClick={createCategory}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Existing categories */}
              <div>
                <label className="text-sm font-medium block mb-2">
                  Categorías existentes ({categories.length})
                </label>
                {categories.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border">
                    No hay categorías creadas
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {categories.map(cat => (
                      <div 
                        key={cat.category_id} 
                        className="flex items-center justify-between p-3 bg-muted/50 border border-border"
                      >
                        <span className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          {cat.name}
                        </span>
                        <button
                          onClick={() => deleteCategory(cat.category_id)}
                          className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-xs text-muted-foreground bg-muted/30 p-3 border border-border">
                Las categorías ayudan a evitar que se muestren dos imágenes similares al mismo tiempo en el carrusel.
              </div>

              {/* Close */}
              <Button variant="outline" onClick={() => setShowCategoryModal(false)} className="w-full">
                Cerrar
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminHero;
